import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { endOfDay, startOfDay, sub } from "date-fns";
import { debounce, round } from "lodash";
import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { useEffect, useState, useCallback } from "react";
import FormatMeasureValue from "./FormatMeasureValue";
import LastMeasuresCard from "./LastMeasuresCard";
import CopyModal from "components/CopyModal";
import { useModal } from "components/hooks";
import MeasureList from "components/MeasureList";
import MeasureLineChart from "components/Measures/MeasureLineChart";
import MeasureSearchForm from "components/Measures/MeasureSearchForm";
import { MeasureToolbarButton } from "components/Measures/MeasureToolbar";
import { toQueryString } from "utils";
import { get } from "utils/io";

const tabs = [
  { index: 0, label: "Chart" },
  { index: 1, label: "Raw Data" },
];

function Hub({
  hubId,
  measureCharts,
  programId,
  isCHF,
  isCKD,
  isHTN,
  aggregateValues,
  deviceTypes,
  alertKeys,
  isDynamicProgram,
}) {
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    takenAfterDate: sub(new Date(), { months: 1 }),
    takenBeforeDate: endOfDay(new Date()),
    macAddress: undefined,
    status: undefined,
    strictPairHub: true,
  });
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [selectedMeasures, setSelectedMeasures] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [autoRefreshMeasure, setAutoRefreshMeasure] = useState(false);
  const [lastMeasures, setLastMeasures] = useState({});
  const [aggregates, setAggregates] = useState({});
  const [tabIndex, setTabIndex] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const {
    isModal: isModalOpen,
    openModal: handleOpenModal,
    closeModal: handleCloseModal,
  } = useModal();

  const handleTabChange = (_, newValue) => setTabIndex(newValue);

  const fetchMeasures = useCallback(
    async (sortField) => {
      setLoading(true);
      const payload = {
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
        esn: hubId,
        taken_after: new Date(searchParams.takenAfterDate).toISOString(),
        taken_before: new Date(searchParams.takenBeforeDate).toISOString(),
        ...(searchParams?.macAddress && {
          device_mac: searchParams.macAddress,
        }),
        ...(searchParams?.status && { status: searchParams.status }),
        hub_filter_option: searchParams.strictPairHub
          ? "only_pair_hub"
          : "pair_hub_or_transmit_hub",
        measure_types: isDynamicProgram
          ? deviceTypes
          : measureCharts.map(({ type }) => type),
        ...(sortField && { ordering: sortField }),
      };

      const queryString = toQueryString(payload);
      try {
        const res = await get(`/measures?${queryString}`);
        const measureData = res?.data?.measures || [];
        const totalCount = res?.data?.total_count || 0;
        setSearchResults(measureData);
        setTotalCount(totalCount);
      } catch (err) {
        enqueueSnackbar("Error fetching measures", { variant: "error" });
      } finally {
        setLoading(false);
      }
    },
    [
      paginationModel,
      hubId,
      searchParams,
      measureCharts,
      isDynamicProgram,
      deviceTypes,
    ]
  );

  const fetchHubDetails = useCallback(async () => {
    const payload = {
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
      measure_types: isDynamicProgram
        ? deviceTypes
        : measureCharts.map(({ type }) => type),
      hub_id: hubId,
      ...(aggregateValues && { include_aggregate_values: true }),
    };

    const queryString = toQueryString(payload);
    const res = await get(`/programs/${programId}/hubs?${queryString}`);
    const hubs = res?.data?.hubs || [];

    if (hubs.length > 0) {
      const hubLastMeasures = hubs[0].last_measures || {};
      const result = Object.keys(hubLastMeasures).reduce((acc, key) => {
        acc[key] = hubLastMeasures[key];
        return acc;
      }, {});

      setLastMeasures(result);
      setAggregates(hubs[0].aggregate_values);
    }
  }, [
    programId,
    hubId,
    paginationModel,
    measureCharts,
    aggregateValues,
    isDynamicProgram,
    deviceTypes,
  ]);

  const handleSearch = (params) => {
    setSearchParams(params);
  };

  const chartData = searchResults
    .map((measure) => ({ ...measure.value, timestamp: measure.timestamp }))
    .reverse();

  const getTicks = (startDate, endDate) => {
    const ticks = [];
    let currDate = startDate;
    while (currDate < endDate) {
      ticks.push(currDate);
      const tempDate = new Date(currDate);
      currDate = startOfDay(tempDate.setDate(tempDate.getDate() + 1)).valueOf();
    }
    return ticks;
  };

  const formatMeasureValue = (type, value) =>
    type === "bloodpressure"
      ? `${round(value.systolic)}/${round(value.diastolic)}, ${round(
          value.bpm
        )} bpm`
      : JSON.stringify(value);

  const onSelectionChanged = (selectionModel) => {
    setSelectedMeasures(selectionModel);
  };

  const refreshMeasureList = () => setPaginationModel({ ...paginationModel });

  const formatMeasuresToCopy = () =>
    searchResults
      .map(
        (measure) =>
          `${new Date(
            measure.timestamp
          ).toLocaleString()}, ${formatMeasureValue(
            measure.type,
            measure.value
          )}`
      )
      .join("\n");

  const updateMeasureGrid = useCallback(() => {
    const debouncedUpdate = debounce(async () => {
      setLoading(true);
      const sortField = sortModel[0]?.field;
      const sortDirection = sortModel[0]?.sort === "desc" ? "-" : "";
      const sortParam = sortField ? `${sortDirection}${sortField}` : undefined;

      await fetchMeasures(sortParam);
      setLoading(false);
    }, 300);

    debouncedUpdate();
    return debouncedUpdate.cancel;
  }, [fetchMeasures, sortModel, setLoading]);

  useEffect(() => {
    fetchHubDetails();
  }, [fetchHubDetails]);

  useEffect(() => {
    if (autoRefreshMeasure) {
      const interval = setInterval(() => {
        updateMeasureGrid();
        fetchHubDetails();
      }, 10000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [autoRefreshMeasure, updateMeasureGrid, fetchHubDetails]);

  useEffect(() => {
    updateMeasureGrid();
  }, [updateMeasureGrid]);

  const dataGridColumns = [
    {
      field: "timestamp",
      headerName: "Time",
      width: 175,
      valueGetter: (params) => new Date(params).toLocaleString(),
    },
    {
      field: "value",
      headerName: "Measure Value",
      width: 175,
      sortable: false,
      renderCell: (params) => (
        <FormatMeasureValue
          type={params.row.type}
          value={params.row.value}
          measureAlert={params.row.measure_alert}
        />
      ),
    },
    {
      field: "hub_id",
      headerName: "Hub ID",
      width: 130,
      renderCell: (params) => params.row.hub?.hub_id,
      sortable: false,
    },
    {
      field: "device_make",
      headerName: "Device Make",
      width: 150,
      renderCell: (params) => params.row.device?.make,
      sortable: false,
    },
    {
      field: "device_model",
      headerName: "Device Model",
      width: 150,
      renderCell: (params) => params.row.device?.model_display_name,
      sortable: false,
    },
    {
      field: "device_mac",
      headerName: "MAC Address",
      sortable: false,
      renderCell: (params) => {
        const { device } = params.row;
        const make = device?.make;
        const model = device?.model_display_name;
        const title = make && model ? `${make} ${model}` : "N/A";
        return (
          <Tooltip
            title={<Box sx={{ fontSize: 13 }}>{title}</Box>}
            placement="right"
            arrow
          >
            <span>{device?.mac_address}</span>
          </Tooltip>
        );
      },
      width: 150,
    },
  ];

  return (
    <Stack spacing={3}>
      <Typography fontWeight="bold" fontSize="20px">
        Hub {hubId}
      </Typography>
      <Box>
        <MeasureSearchForm
          onSubmit={handleSearch}
          searchCriterias={{
            hubId: false,
            macAddress: true,
            status: false,
            strictPairHub: true,
          }}
        />
        <LastMeasuresCard
          lastMeasures={lastMeasures}
          alertKeys={alertKeys}
          aggregates={aggregates}
          isCKD={isCKD}
          isCHF={isCHF}
          isHTN={isHTN}
          isDynamicProgram={isDynamicProgram}
        />
      </Box>
      <Stack sx={{ border: `1px solid ${grey[400]}`, borderRadius: 1 }}>
        <Tabs value={tabIndex} onChange={handleTabChange}>
          {tabs.map((tab) => (
            <Tab key={tab.index} label={tab.label} />
          ))}
        </Tabs>

        <TabPanel tabIndex={tabIndex} index={0}>
          {(() => {
            if (loading) {
              return (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress />
                </Box>
              );
            }

            if (chartData.length === 0) {
              return (
                <Typography sx={{ color: grey[500] }}>
                  No Measurements Found
                </Typography>
              );
            }

            return (
              <MeasureLineChart
                data={chartData}
                isShowExportCSVButton={false}
                graphs={measureCharts}
                lineChartProps={{
                  margin: { left: -20 },
                }}
                labelProps={{ sx: { mt: "10px" } }}
                XAxisProps={{
                  ticks: getTicks(
                    new Date(chartData[0].timestamp).valueOf(),
                    endOfDay(
                      new Date(chartData[chartData.length - 1].timestamp)
                    ).valueOf()
                  ),
                  padding: { left: 50, right: 30 },
                }}
              />
            );
          })()}
        </TabPanel>

        <TabPanel tabIndex={tabIndex} index={1}>
          <MeasureList
            columnsConfig={dataGridColumns}
            rows={searchResults}
            loading={loading}
            onSelectionChanged={onSelectionChanged}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            rowsPerPageOptions={[20, 25, 50, 100]}
            rowCount={totalCount}
            refreshMeasureList={refreshMeasureList}
            selectedMeasures={selectedMeasures}
            searchResults={searchResults}
            setAutoRefreshMeasure={setAutoRefreshMeasure}
            autoRefreshMeasure={autoRefreshMeasure}
            setSortModel={setSortModel}
            sortingMode="server"
            showDeliveries={false}
            toolbarProps={{
              visibleToolbarItems: { autoRefresh: true },
              customComponents: [
                {
                  name: "copyButton",
                  value: (
                    <>
                      <MeasureToolbarButton
                        text="Copy"
                        onClick={handleOpenModal}
                      />
                      <CopyModal
                        open={isModalOpen}
                        onClose={handleCloseModal}
                        text={formatMeasuresToCopy()}
                      />
                    </>
                  ),
                },
              ],
            }}
          />
        </TabPanel>
      </Stack>
    </Stack>
  );
}

Hub.propTypes = {
  hubId: PropTypes.string.isRequired,
  programId: PropTypes.string.isRequired,
  measureCharts: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          color: PropTypes.string,
          bounds: PropTypes.arrayOf(
            PropTypes.oneOfType([
              PropTypes.number,
              PropTypes.shape({
                color: PropTypes.string,
                value: PropTypes.number,
              }),
            ])
          ),
        })
      ),
    })
  ).isRequired,
  isCHF: PropTypes.bool,
  isCKD: PropTypes.bool,
  isHTN: PropTypes.bool,
  aggregateValues: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  deviceTypes: PropTypes.arrayOf(PropTypes.string),
  alertKeys: PropTypes.arrayOf(PropTypes.string),
  isDynamicProgram: PropTypes.bool,
};

Hub.defaultProps = {
  isCHF: false,
  isCKD: false,
  isHTN: false,
  aggregateValues: null,
  deviceTypes: [],
  alertKeys: [],
  isDynamicProgram: false,
};

function TabPanel(props) {
  const { children, tabIndex, index, ...other } = props;

  return (
    <Box hidden={tabIndex !== index} id={`tab-panel-${index}`} {...other}>
      {tabIndex === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </Box>
  );
}

TabPanel.propTypes = {
  tabIndex: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
};

export default Hub;
